import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

export type PillSize = 'medium' | 'small';

interface PillProps {
  label: string;
  /** Fully rounded corners. */
  rounded?: boolean;
  size?: PillSize;
  /** When true, renders `small` below the MD breakpoint and `medium` at MD+. */
  responsive?: boolean;
  className?: string;
}

// bricks default Pill: white background, primary border, tertiary label text.
// Heights/paddings are intrinsic (text + padding) since bricks' fixed 33px
// height has no clean Tailwind token.
const SIZE_STYLES: Record<PillSize, string> = {
  medium: 'px-3 py-1',
  small: 'px-2 py-0.5',
};

const SIZE_TEXT: Record<PillSize, string> = {
  medium: 'text-body-md',
  small: 'text-body-sm',
};

const RESPONSIVE_STYLES = 'px-2 py-0.5 md:px-3 md:py-1';
const RESPONSIVE_TEXT = 'text-body-sm md:text-body-md';

/** Status pill built on atoms primitives. Replaces the bricks-next Pill. */
export const Pill: React.FC<PillProps> = ({
  label,
  rounded,
  size = 'medium',
  responsive,
  className,
}) => {
  return (
    <Box
      className={cn(
        'w-max items-center justify-center border border-od-border-primary bg-od-bg-primary',
        rounded ? 'rounded-full' : 'rounded',
        responsive ? RESPONSIVE_STYLES : SIZE_STYLES[size],
        className,
      )}
    >
      <Text
        as="span"
        className={cn(responsive ? RESPONSIVE_TEXT : SIZE_TEXT[size], 'text-od-content-tertiary')}
      >
        {label}
      </Text>
    </Box>
  );
};
